/**
 * DeleteSchoolDialog — irreversible cascade-delete flow for a school.
 *
 * Safety:
 *  - User must type the EXACT school name to enable the delete button
 *  - Reason field (optional but recommended; logged in audit trail)
 *  - Shows the cascade summary (sessions / students / payments) before action
 *  - Server also re-validates the typed name on POST (defence in depth)
 */
import React, { useState } from "react";
import { Trash, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DeleteSchoolDialog({ open, onOpenChange, school, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) { setTyped(""); setReason(""); }
  }, [open]);

  if (!school) return null;
  const matches = typed.trim() === (school.school_name || "");

  const onDelete = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/super-admin/schools/${school.id}`, {
        data: { confirm_school_name: typed.trim(), reason: reason.trim() || null },
      });
      toast.success(`Deleted ${data.total_docs_deleted} records for ${data.school_name}`);
      onDeleted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="delete-school-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-rose-800">
            <Warning size={20} weight="duotone" /> Delete school permanently
          </DialogTitle>
          <DialogDescription>
            This deletes <strong>{school.school_name}</strong> and all its tenant data.
            <span className="block mt-1 text-rose-700 font-semibold">Cannot be undone.</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-xs text-slate-600">
          <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-md p-3 space-y-1">
            <div className="font-semibold flex items-center gap-1.5">
              <Trash size={13} /> The following will be permanently removed:
            </div>
            <ul className="list-disc ml-5 space-y-0.5">
              <li>Every academic session, class, and student record</li>
              <li>All fee structures, payments, and receipts</li>
              <li>All school admins and teachers (super admin accounts are preserved)</li>
              <li>All audit logs for this school</li>
              <li>The school's receipt-number counters</li>
            </ul>
          </div>

          <div>
            <Label className="text-xs text-slate-700">
              Type <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-900">{school.school_name}</code> to confirm
            </Label>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={school.school_name}
              className="mt-1.5"
              data-testid="delete-school-confirm-input"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Reason (optional, audit-logged)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Test school cleanup"
              className="mt-1.5"
              data-testid="delete-school-reason-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={onDelete}
            disabled={!matches || busy}
            className="bg-rose-700 hover:bg-rose-800 gap-1.5"
            data-testid="delete-school-confirm-btn"
          >
            <Trash size={14} weight="fill" /> {busy ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
